/*
  assets/details-modal.js — DETAILS-MODAL-02
  Owner: existing <details-modal> base class and password-modal inheritance contract.
*/
class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = null;
    this.summaryToggle = null;
    this.closeButton = null;
    this.releaseFocusTrap = null;
    this.abortController = null;
    this.onBodyClickEvent = this.onBodyClick.bind(this);
  }

  connectedCallback() {
    if (this.abortController) return;

    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.detailsContainer
      ? this.detailsContainer.querySelector('summary')
      : null;
    this.closeButton = this.querySelector('button[type="button"]');

    if (!this.detailsContainer || !this.summaryToggle) return;

    this.abortController = typeof AbortController === 'function'
      ? new AbortController()
      : null;
    var options = this.abortController ? { signal: this.abortController.signal } : undefined;

    this.detailsContainer.addEventListener('keyup', (event) => {
      if (event.key === 'Escape' || String(event.code || '').toUpperCase() === 'ESCAPE') {
        event.preventDefault();
        this.close();
      }
    }, options);

    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this), options);
    if (this.closeButton) {
      this.closeButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.close();
      }, options);
    }

    this.summaryToggle.setAttribute(
      'aria-expanded',
      this.detailsContainer.open ? 'true' : 'false'
    );
  }

  disconnectedCallback() {
    this.close(false);
    if (this.abortController) this.abortController.abort();
    this.abortController = null;
  }

  isOpen() {
    return Boolean(this.detailsContainer && this.detailsContainer.open);
  }

  onSummaryClick(event) {
    event.preventDefault();
    this.isOpen() ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.isOpen()) return;
    if (!this.contains(event.target)) this.close();
  }

  lockPage() {
    var count = Number(document.documentElement.dataset.detailsModalOpenCount || 0);
    document.documentElement.dataset.detailsModalOpenCount = String(count + 1);
    document.body.classList.add('overflow-hidden');
  }

  unlockPage() {
    var count = Math.max(
      0,
      Number(document.documentElement.dataset.detailsModalOpenCount || 0) - 1
    );

    if (count) {
      document.documentElement.dataset.detailsModalOpenCount = String(count);
      return;
    }

    document.documentElement.removeAttribute('data-details-modal-open-count');
    if (!document.querySelector('cart-drawer.active, details-modal details[open], password-modal details[open]')) {
      document.body.classList.remove('overflow-hidden');
    }
  }

  open(event) {
    if (!this.detailsContainer || this.isOpen()) return;

    var eventTarget = event && event.target && event.target.closest
      ? event.target.closest('details')
      : null;
    var details = eventTarget || this.detailsContainer;
    if (details !== this.detailsContainer) return;

    details.setAttribute('open', '');
    this.summaryToggle.setAttribute('aria-expanded', 'true');
    document.body.addEventListener('click', this.onBodyClickEvent);
    this.lockPage();

    if (typeof this.releaseFocusTrap === 'function') this.releaseFocusTrap();
    this.releaseFocusTrap = null;

    var trapContainer = this.detailsContainer.querySelector('[tabindex="-1"]')
      || this.detailsContainer.querySelector('[role="dialog"]')
      || this.detailsContainer;
    var focusTarget = this.detailsContainer.querySelector('input:not([type="hidden"]), button:not([disabled]), [href]')
      || trapContainer;

    if (typeof trapFocus === 'function' && trapContainer) {
      var release = trapFocus(trapContainer, focusTarget);
      if (typeof release === 'function') this.releaseFocusTrap = release;
    } else if (focusTarget && typeof focusTarget.focus === 'function') {
      try {
        focusTarget.focus({ preventScroll: true });
      } catch (_error) {
        focusTarget.focus();
      }
    }
  }

  close(focusToggle = true) {
    if (!this.detailsContainer || !this.isOpen()) return;

    if (typeof this.releaseFocusTrap === 'function') this.releaseFocusTrap();
    this.releaseFocusTrap = null;

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(focusToggle ? this.summaryToggle : null);
    } else if (focusToggle && this.summaryToggle) {
      try {
        this.summaryToggle.focus({ preventScroll: true });
      } catch (_error) {
        this.summaryToggle.focus();
      }
    }

    this.detailsContainer.removeAttribute('open');
    this.summaryToggle.setAttribute('aria-expanded', 'false');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    this.unlockPage();
  }
}

window.DetailsModal = DetailsModal;

if (!customElements.get('details-modal')) {
  customElements.define('details-modal', DetailsModal);
}
